<div>
  <div class="flex flex-col gap-5 md:flex-row md:items-end">
    <div>
      <h1 class="text-2xl font-semibold dark:text-white-light md:text-3xl">{{ 'recon_data' | translate }}</h1>
    </div>
  </div>

  <div class="mt-5 grid gap-4 grid-cols-1 md:grid-cols-12">
    <div class="md:col-span-3">
      <app-debounce-input
        (valueChange)="onInputChanged($event)"
        [delay]="600"
        class="w-full"
      ></app-debounce-input>
    </div>

    <div class="md:col-span-3" *ngIf='providers.length > 0'>
      <ng-select
        [items]="providers"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        placeholder="Select Provider"
        class="custom-multiselect w-full"
        [(ngModel)]="params.code"
        (change)="onProviderChange($event)"
      >
      </ng-select>
    </div>

    <div class="md:col-span-3">
      <ng2-flatpickr
        #dateFromPicker
        [config]="dateFromConfig"
        placeholder="Select Date"
        (change)="onCreatedFromChange($event)"
        addClass="form-input">
      </ng2-flatpickr>
    </div>

    <!--
      Status filter — applies to the recons table (PDAM file rows).
      Values match recons.status in DB:
        '' (default)  = no filter, show all
        '0'           = Success / Matched (paired with a transaction by Match step)
        '1'           = Pending (PDAM has it, but not yet reconciled to a local or apidash tx)
    -->
    <div class="md:col-span-3">
      <ng-select
        [items]="statusOptions"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        placeholder="Filter Status"
        class="custom-multiselect w-full"
        [(ngModel)]="params.status"
        (change)="onStatusChange()"
      >
      </ng-select>
    </div>
  </div>

  <!--
    Tabs: Recon Data | Unmatched Transactions
    Active tab tracked by activeTab ('recon' | 'unmatched').
  -->
  <div class="mt-4 mb-2 flex border-b border-[#e0e6ed] dark:border-[#1b2e4b]">
    <button
      type="button"
      class="px-4 py-2 -mb-px border-b-2 text-sm font-semibold"
      [class.border-primary]="activeTab === 'recon'"
      [class.text-primary]="activeTab === 'recon'"
      [class.border-transparent]="activeTab !== 'recon'"
      (click)="activeTab = 'recon'"
    >Recon Data (PDAM)</button>
    <button
      type="button"
      class="px-4 py-2 -mb-px border-b-2 text-sm font-semibold"
      [class.border-primary]="activeTab === 'unmatched'"
      [class.text-primary]="activeTab === 'unmatched'"
      [class.border-transparent]="activeTab !== 'unmatched'"
      (click)="onUnmatchedTabOpen()"
    >
      Unmatched Transactions
      <span *ngIf="unmatchedTotal > 0"
            class="ml-1 inline-block min-w-[1.5rem] rounded-full bg-danger text-white text-xs font-bold px-2 py-0.5">
        {{ unmatchedTotal }}
      </span>
    </button>
  </div>

  <!-- ───────────────────── Tab 1: Recon Data ───────────────────── -->
  <div class="panel mt-2 pb-0" *ngIf="activeTab === 'recon'">
    <div class="m-1 flex justify-end mb-2 gap-2">
      <!--
        Tarik Data PDAM — admin-only async sync.
        Disabled if a provider+date isn't selected, OR if a sync is in
        flight (syncing stays true while we poll job status).
      -->
      <button
        type="button"
        class="btn btn-success btn-sm"
        [disabled]="!params.code || syncing || matching"
        (click)="triggerSync()"
      >
        <span *ngIf="!syncing">📥 Tarik Data PDAM</span>
        <span *ngIf="syncing" class="inline-flex items-center gap-2">
          <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-l-transparent"></span>
          Tarik Data...
        </span>
      </button>

      <!--
        Match dengan Transaksi — runs the JOIN matcher. Re-runnable;
        clicking again wipes prior matches for this (code, date).
      -->
      <button
        type="button"
        class="btn btn-warning btn-sm"
        [disabled]="!params.code || syncing || matching"
        (click)="runMatch()"
      >
        <span *ngIf="!matching">🔄 Match dengan Transaksi</span>
        <span *ngIf="matching" class="inline-flex items-center gap-2">
          <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-l-transparent"></span>
          Matching...
        </span>
      </button>

      <div class="btn btn-primary btn-sm" (click)="downloadExcel()">
        <icon-code-file class="ltr:mr-2 rtl:ml-2" />
        EXCEL
      </div>

      <!--
        Bank Mandiri reconciliation reports — two Excel files (VA + PPOB).
        Both buttons are only enabled when:
          - A provider+date is selected (params.code & params.from_date)
          - All recons for that (code, date) are matched (status=0)
            i.e. the BE returns all_matched=true.
        Disabled state shows a hint via [title] tooltip.
      -->
      <button
        type="button"
        class="btn btn-info btn-sm"
        [disabled]="!params.code || downloadingBM"
        [title]="bankMandiriDisabledHint('va')"
        (click)="downloadBankMandiriVA()"
      >
        <span *ngIf="!downloadingBM">📄 VA Bank Mandiri</span>
        <span *ngIf="downloadingBM" class="inline-flex items-center gap-2">
          <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-l-transparent"></span>
          ...
        </span>
      </button>

      <button
        type="button"
        class="btn btn-info btn-sm"
        [disabled]="!params.code || downloadingBM"
        [title]="bankMandiriDisabledHint('ppob')"
        (click)="downloadBankMandiriPPOB()"
      >
        <span *ngIf="!downloadingBM">📄 PPOB Bank Mandiri</span>
        <span *ngIf="downloadingBM" class="inline-flex items-center gap-2">
          <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-l-transparent"></span>
          ...
        </span>
      </button>
    </div>
    <div class="datatable">
      <ng-datatable
        [isServerMode]="true"
        [rows]="rows"
        [columns]="cols"
        [loading]="loading"
        [totalRows]="totalRows"
        [pageSize]="params.rowPerPage"
        [search]="params.filter"
        [pageSizeOptions]="pageSizeOptions"
        (changeServer)="changeServer($event)"
        skin="whitespace-nowrap table-hover"
        firstArrow='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5 rtl:rotate-180"> <path d="M13 19L7 12L13 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path opacity="0.5" d="M16.9998 19L10.9998 12L16.9998 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>'
        lastArrow='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5 rtl:rotate-180"> <path d="M11 19L17 12L11 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path opacity="0.5" d="M6.99976 19L12.9998 12L6.99976 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg> '
        previousArrow='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5 rtl:rotate-180"> <path d="M15 5L9 12L15 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>'
        nextArrow='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4.5 h-4.5 rtl:rotate-180"> <path d="M9 5L15 12L9 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>'
      >
        <ng-template slot="amount" let-value="data">
          <div class='text-end'>{{ value.amount | number:'1.0-0' }}</div>
        </ng-template>

        <ng-template slot="trx_date" let-value="data">
          <div class='text-end'>{{ value.trx_date | date: 'yyyy-MM-dd HH:mm:ss' }}</div>
        </ng-template>

        <ng-template slot="status" let-value="data">
          <span class="badge" [ngClass]="{
              'bg-danger': value.status === '1',
              'bg-success': value.status !== '1'
            }">
            {{ value.status === '1' ? 'Pending' : 'Success' }}
          </span>
        </ng-template>
      </ng-datatable>
    </div>
  </div>

  <!-- ───────────────────── Tab 2: Unmatched Transactions ───────────────────── -->
  <div class="panel mt-2 pb-0" *ngIf="activeTab === 'unmatched'">
    <div class="m-1 mb-3">
      <p class="text-sm text-gray-600 dark:text-gray-300">
        🚨 <b>Alert list:</b> Transaksi <i>sukses</i> kita yang belum punya
        baris match di file recon PDAM untuk
        <b>{{ params.code }}</b> tanggal
        <b>{{ params.from_date | date:'yyyy-MM-dd' }}</b>.
        Pastikan sudah tarik data PDAM untuk tanggal tersebut, lalu klik
        <i>Match dengan Transaksi</i> di tab sebelah.
      </p>
    </div>
    <div *ngIf="unmatchedLoading" class="p-6 text-center">
      <span class="inline-block h-6 w-6 animate-spin rounded-full border-2 border-primary border-l-transparent"></span>
    </div>
    <div *ngIf="!unmatchedLoading && unmatchedRows.length === 0" class="p-6 text-center text-success font-semibold">
      ✅ Semua transaksi sukses sudah ke-recon. Tidak ada anomali.
    </div>
    <table *ngIf="!unmatchedLoading && unmatchedRows.length > 0" class="table table-hover whitespace-nowrap">
      <thead>
        <tr>
          <th>TX ID</th>
          <th>Bill ID</th>
          <th>Period</th>
          <th class="text-end">Bill Amount</th>
          <th class="text-end">Total TX Amount</th>
          <th>Merchant</th>
          <th>Created (WIB)</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of unmatchedRows">
          <td><code class="text-xs">{{ row.tx_id }}</code></td>
          <td>{{ row.bill_id }}</td>
          <td>{{ row.period }}</td>
          <td class="text-end">{{ row.bill_amount | number:'1.0-0' }}</td>
          <td class="text-end">{{ row.transaction_amount | number:'1.0-0' }}</td>
          <td>{{ row.merchant_code }}</td>
          <td>{{ row.created_at | date:'yyyy-MM-dd HH:mm:ss' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
